import React from "react";
import { Calendar } from "lucide-react";
import WorkHoursDatePopover from "./WorkHoursDatePopover";

interface ScheduledDateFieldProps {
  value: string | null;
  onChange: (date: string | null) => void;
  restrictToWorkingHours: boolean;
  weekStartDay?: number | null;
  ariaLabel?: string;
}

/**
 * The scheduled-date input used in both the task and subtask panels.
 * Switches between the native <input type="date"> and the
 * WorkHoursDatePopover (which disables weekend cells) based on
 * `restrictToWorkingHours`. Native date inputs can't disable individual
 * weekdays, so the custom popover is the only way to make weekends
 * literally unreachable for working-hours-only entries.
 *
 * Caller's onChange runs the field-clear cascade (which differs for task
 * vs subtask, since tasks also need to clear recurrence/persistent when
 * the date is removed).
 */
const ScheduledDateField: React.FC<ScheduledDateFieldProps> = ({
  value,
  onChange,
  restrictToWorkingHours,
  weekStartDay,
  ariaLabel = "Scheduled date",
}) => {
  if (restrictToWorkingHours) {
    return (
      <WorkHoursDatePopover
        value={value}
        onChange={onChange}
        ariaLabel={ariaLabel}
        weekStartDay={weekStartDay}
      />
    );
  }
  return (
    <>
      <Calendar
        className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-text-tertiary"
        size={16}
      />
      <input
        type="date"
        aria-label={ariaLabel}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-warm-bg-elevated border border-warm-border rounded-soft pl-10 pr-3 py-2 text-sm text-warm-text-primary focus:outline-none focus:ring-2 focus:ring-warm-blue"
      />
    </>
  );
};

export default ScheduledDateField;
